"use client";

import React, { useState, useId } from "react";
import { MapPin, Search } from "lucide-react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
interface Community {
  id: string;
  name: string;
  description: string;
}
const HOWARD_COMMUNITIES: Community[] = [{
  id: "columbia",
  name: "Columbia",
  description: "Planned community along Route 29"
}, {
  id: "ellicott-city",
  name: "Ellicott City",
  description: "Historic mill town & Route 40 commercial corridor"
}, {
  id: "clarksville",
  name: "Clarksville",
  description: "Residential estates & Route 108 corridor"
}, {
  id: "fulton",
  name: "Fulton",
  description: "South Howard community near Maple Lawn"
}, {
  id: "maple-lawn",
  name: "Maple Lawn",
  description: "Live-work-play mixed community in Fulton"
}, {
  id: "jessup",
  name: "Jessup",
  description: "Route 1 logistics & industrial distribution hub"
}, {
  id: "elkridge",
  name: "Elkridge",
  description: "Northern Howard residential and commercial center"
}, {
  id: "savage",
  name: "Savage",
  description: "Historic mill area along the Little Patuxent River"
}, {
  id: "highland",
  name: "Highland",
  description: "Western agricultural & rural residential zone"
}];
export default function HowardCountyCommunities() {
  const [searchTerm, setSearchTerm] = useState("");
  const [gridRef] = useAutoAnimate<HTMLDivElement>();
  const searchInputId = useId();
  const filteredCommunities = HOWARD_COMMUNITIES.filter(community => community.name.toLowerCase().includes(searchTerm.trim().toLowerCase()));
  return <section id="howardcountycommunities" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center space-y-4 mb-10 md:mb-12">
          <div>
            <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold border-primary/30 text-primary bg-primary/5 px-3 py-1">
              Howard County Service Area
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Cities & Communities We Serve in Howard County
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Local rapid-response crews dispatched 24/7 across every neighborhood and commercial corridor in Howard County.
          </p>

          {/* Search Filter Input */}
          <div className="pt-4 max-w-md mx-auto">
            <div className="relative">
              <label htmlFor={searchInputId} className="sr-only">
                Search your city
              </label>
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
              <Input id={searchInputId} type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search your city..." className="pl-10 pr-4 h-11 bg-card rounded-md border-border shadow-2xs focus-visible:ring-primary focus-visible:border-primary text-foreground placeholder:text-muted-foreground text-sm" />
            </div>
          </div>
        </div>

        {/* Communities Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
          {filteredCommunities.map((community, i) => <div key={community.id} data-index={i} className="h-full">
              <Card className="h-full bg-card border border-border rounded-lg shadow-2xs hover:shadow-md hover:-translate-y-0.5 hover:border-primary/60 transition-all duration-200 flex flex-col justify-between py-5 gap-2">
                <CardHeader className="px-5 pb-2">
                  <div className="flex items-center gap-2 mb-1.5 text-primary">
                    <MapPin className="size-4 shrink-0" aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Howard Co.
                    </span>
                  </div>
                  <CardTitle className="text-lg md:text-xl font-bold tracking-tight text-foreground select-none">
                    {community.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pt-0">
                  <CardDescription className="text-xs md:text-sm text-muted-foreground leading-snug">
                    {community.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {/* Empty State */}
        {filteredCommunities.length === 0 && <div className="py-12">
            <Card className="max-w-md mx-auto text-center border-dashed border-border bg-muted/40 py-8 px-6">
              <CardContent className="space-y-2 p-0">
                <p className="text-sm md:text-base font-medium text-foreground">
                  No matching community found in Howard County.
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Call dispatch directly for coverage throughout surrounding areas.
                </p>
              </CardContent>
            </Card>
          </div>}

        {/* Call-to-action Block */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
          <p className="text-base font-medium text-foreground">
            Need emergency restoration services?
          </p>
          <Button asChild size="lg" className="shadow-sm font-semibold">
            <Link to="/ContactRequestServicePage">Request Emergency Service</Link>
          </Button>
        </div>
      </div>
    </section>;
}